import { Logger } from "./logger";
import { DQ_ID_STORAGE_KEY, DqTagEnvHost, DqTagEventApi } from "./config";
import {
  type DqTagConfig,
  type TCFApi,
  type BaseEvent,
  type WitnessEventData,
  type ProfileEventData,
  type IdentifyEventData,
  type DqTag,
  type DqTagCommandExecutor,
} from "./models";
import { ensurePresent, generateRandomString, isValidUrl } from "./utils";
import { ConsentProcessor } from "./consentProcessor";

declare global {
  interface Window {
    dqtag: {
      cmd: DqTagCommandExecutor[];
      instance?: DqTag;
    };
    __tcfapi?: TCFApi;
  }
}

class _DqTag implements DqTag {
  private _clientId: string;
  private _apiUrl: string;
  private _logger: Logger;
  private _consentProcessor: ConsentProcessor;
  private _initialized = false;

  constructor() {
    this._clientId = "";
    this._apiUrl = DqTagEnvHost.prod;
    this._logger = new Logger();
    this._consentProcessor = new ConsentProcessor(this._logger);
  }

  get currentOrigin(): string {
    return globalThis.location.origin;
  }

  get currentHref(): string {
    return window.location.href.split("?")[0].split("#")[0];
  }

  private get dqId(): string | null {
    return localStorage.getItem(DQ_ID_STORAGE_KEY) ?? null;
  }

  private set dqId(value: string | null) {
    if (value == null) {
      localStorage.removeItem(DQ_ID_STORAGE_KEY);
      return;
    }
    localStorage.setItem(DQ_ID_STORAGE_KEY, value);
  }

  public initialize(config: DqTagConfig): void {
    this.applyConfiguration(config);
    void this._consentProcessor.initialize();
    this._initialized = true;
  }

  public async logPageViewWitness(): Promise<Response | undefined> {
    return this.logWitness({
      type: "page_view",
      properties: {
        url: this.currentHref,
      },
    });
  }

  public async logPageView(
    userId: string,
    idName: string,
  ): Promise<Awaited<Response | undefined>[] | undefined> {
    if (!userId) {
      this._logger.printError("userId is required");
      return;
    }
    if (!idName) {
      this._logger.printError("idName is required");
      return;
    }
    return Promise.all([
      this.logWitness({
        type: "page_view",
        properties: {
          url: this.currentHref,
        },
      }),
      this.logIdentify({
        [idName]: userId,
      }),
    ]);
  }

  public async logWitness(
    eventData: WitnessEventData,
  ): Promise<Response | undefined> {
    if (typeof eventData !== "object" || !eventData) {
      this._logger.printError("Invalid data for witness event");
    }
    return this.submitEvent(DqTagEventApi.witness, eventData);
  }

  public async logIdentify(
    eventData: IdentifyEventData,
  ): Promise<Response | undefined> {
    if (typeof eventData !== "object" || !eventData) {
      this._logger.printError("Invalid data for identify event");
    }
    return this.submitEvent(DqTagEventApi.identify, eventData);
  }

  public async logProfile(
    eventData: ProfileEventData,
  ): Promise<Response | undefined> {
    if (typeof eventData !== "object" || !eventData) {
      this._logger.printError("Invalid data for profile event");
    }
    return this.submitEvent(DqTagEventApi.profile, eventData);
  }

  public flushCommandQueue(): void {
    window.dqtag = window.dqtag ?? {};
    window.dqtag.cmd = window.dqtag.cmd ?? [];
    const logger = this._logger;
    // TODO: replace method patching with command processor that exposes method push
    window.dqtag.cmd.push = function (...args: DqTagCommandExecutor[]): number {
      if (args.length === 0) {
        return 0;
      }
      if (!args.every((fn) => typeof fn === "function")) {
        logger.printError("DqTag: All commands must be functions");
        return 0;
      }
      args.forEach((fn) => {
        try {
          if (!window.dqtag.instance) {
            throw new Error(
              "Instance is not initialized, unable to execute command",
            );
          }
          fn(window.dqtag.instance);
        } catch (error) {
          logger.printError("DQTag: ", error);
        }
      });
      return 0;
    };
    while (window.dqtag.cmd.length > 0) {
      const fn = window.dqtag.cmd.shift();
      if (!fn) continue;
      window.dqtag.cmd.push(fn);
    }
  }

  private applyConfiguration(config: DqTagConfig): void {
    try {
      if (!config || typeof config !== "object") {
        throw new Error(
          "`config` must be an object holding a `clientId` property",
        );
      }
      const {
        clientId,
        debug = false,
        disableTcf = false,
        host = DqTagEnvHost.prod,
      } = config;
      if (!clientId || clientId.trim() === "") {
        throw new Error("`clientId` is required and cannot be empty");
      }
      this._clientId = config.clientId;
      if (host) {
        if (typeof host !== "string") {
          throw new Error("`host` must be a string");
        }
        if (!isValidUrl(host)) {
          throw new Error("`host` must be a valid URL");
        }
        this._apiUrl = host;
      }
      if (disableTcf !== undefined && typeof disableTcf === "boolean") {
        this._consentProcessor.disabled = disableTcf;
        if (disableTcf) {
          this._logger.printDebug("TCF is disabled");
        }
      }
      if (debug !== undefined && typeof debug === "boolean") {
        this._logger.debug = debug;
        this._logger.printDebug("Debug mode is enabled");
      }
    } catch (error) {
      this._logger.printError(error instanceof Error ? error.message : error);
    }
  }

  private async submitEvent(
    eventApi: string,
    eventData: BaseEvent["event"],
  ): Promise<Response | undefined> {
    try {
      if (!eventData || typeof eventData !== "object") {
        throw new Error("Invalid event data provided");
      }
      if (!this._clientId || !eventApi) {
        throw new Error("Client ID or dqId or eventApi is not set");
      }
      this._logger.printDebug(
        "Awaiting for DqTag to be initialized & TCF data loaded",
      );
      await ensurePresent(
        () =>
          (this._initialized &&
            (this._consentProcessor.disabled ||
              this._consentProcessor.loaded)) ||
          null,
        // We might want to set reasonable timeout here
        Infinity,
      );
      this.dqId = this._consentProcessor.hasConsentOrNotApplied
        ? (this.dqId ?? generateRandomString())
        : null;
      const event: BaseEvent = {
        api: this._consentProcessor.hasConsentOrNotApplied
          ? eventApi
          : DqTagEventApi.log,
        timestamp: Date.now(),
        dqId: this._consentProcessor.hasConsentOrNotApplied
          ? (this.dqId ?? "")
          : "",
        clientId: this._clientId,
        site: this.currentOrigin,
        consent: this._consentProcessor.payload,
        event: this._consentProcessor.hasConsentOrNotApplied
          ? eventData
          : { msg: "invalid consent" },
      };
      const requestInit: RequestInit = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(event),
      };
      this._logger.printDebug(
        `Sending log event to ${this._apiUrl}:`,
        requestInit,
      );
      const response = await fetch(this._apiUrl, requestInit);
      this._logger.printDebug(
        `DQTag: Received response from ${this._apiUrl}:`,
        {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
        },
      );
      if (!response.ok) {
        this._logger.printError(
          `DQTag.logEvent: HTTP error. Status: ${response.status}`,
        );
      }
      return response;
    } catch (error) {
      this._logger.printError(error);
      return undefined;
    }
  }
}

(function () {
  if (window.dqtag.instance) {
    return;
  }
  const dqTag = new _DqTag();
  window.dqtag = window.dqtag ?? {};
  window.dqtag.instance = dqTag;
  dqTag.flushCommandQueue();
})();
